/**
 * ReportLegalDisclaimer — always-on end-of-report legal notice.
 *
 * Renders after the blind-spots appendix and before the end-of-report
 * stamp + theme footer. Print/PDF export inherits this block via
 * SiteRagReportView; DOCX export mirrors the same copy in
 * reportDocxExport.ts.
 */
import React from 'react';
import type { ReportTheme } from '../../../types/reportTheme';
import { roleColor } from '../../../lib/reportRoleColors';
import type { ReportConflictNotice } from '../../../lib/reportConflictNotice';
import {
  REPORT_LEGAL_DISCLAIMER_HEADING,
  REPORT_LEGAL_DISCLAIMER_PARAGRAPHS,
} from '../../../lib/reportLegalDisclaimer';

const MONO = 'var(--report-body-font, inherit)';

export interface ReportLegalDisclaimerProps {
  embedded?: boolean;
  style?: React.CSSProperties;
  theme?: ReportTheme;
  /**
   * Pre-resolved unresolved-conflict notice (see `reportConflictNotice.ts`).
   * Passed in rather than derived here so this component stays presentational and
   * the SAME notice text reaches the reader, the PDF DOM, DOCX and PPTX.
   * `null`/omitted renders nothing, keeping unaffected reports byte-stable.
   */
  conflictNotice?: ReportConflictNotice | null;
}

export const ReportLegalDisclaimer: React.FC<ReportLegalDisclaimerProps> = ({
  embedded = false,
  style,
  theme,
  conflictNotice,
}) => {
  const typographyStyle: React.CSSProperties | undefined = theme
    ? { fontFamily: theme.bodyFont, color: roleColor(theme, 'body') }
    : undefined;

  const body = (
    <>
      {/* Editorial small-caps mono head in the theme accent ink (was a
          slate uppercase h3). */}
      <h3
        className="text-sm font-bold uppercase tracking-widest text-text-secondary"
        style={{
          fontFamily: MONO,
          letterSpacing: '0.16em',
          color: roleColor(theme, 'accent'),
          ...(theme ? { fontFamily: MONO } : {}),
        }}
      >
        {REPORT_LEGAL_DISCLAIMER_HEADING}
      </h3>
      {REPORT_LEGAL_DISCLAIMER_PARAGRAPHS.map((paragraph, i) => (
        <p
          key={i}
          className="text-xs leading-relaxed text-text-secondary"
          style={{ color: roleColor(theme, 'muted') }}
          data-legal-paragraph="true"
          data-testid={`report-legal-disclaimer-p-${i}`}
        >
          {paragraph}
        </p>
      ))}
      {/*
        Conditional unresolved-conflict notice. NOT part of
        REPORT_LEGAL_DISCLAIMER_PARAGRAPHS — those are asserted verbatim by
        validatePdfArtifact and pinned by the export smokes, so it renders as its
        own block and is absent entirely for unaffected reports. `data-legal-
        paragraph` is deliberately NOT stamped so the PDF validator's paragraph
        count is unchanged.
      */}
      {conflictNotice ? (
        <div className="mt-4" data-testid="report-conflict-notice">
          <h3
            className="text-xs font-semibold uppercase tracking-widest"
            style={{ color: roleColor(theme, 'warning') }}
          >
            {conflictNotice.heading}
          </h3>
          <p
            className="mt-1 text-xs leading-relaxed"
            style={{ color: roleColor(theme, 'muted') }}
            data-testid="report-conflict-notice-body"
          >
            {conflictNotice.body}
          </p>
        </div>
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <div
        className="space-y-2.5"
        data-testid="report-legal-disclaimer"
        aria-label={REPORT_LEGAL_DISCLAIMER_HEADING}
        data-legal-paragraph-count={REPORT_LEGAL_DISCLAIMER_PARAGRAPHS.length}
        style={{ ...typographyStyle, ...style }}
      >
        {body}
      </div>
    );
  }

  return (
    <aside
      id="legal-disclaimer"
      data-section="legalDisclaimer"
      data-testid="report-legal-disclaimer"
      aria-label={REPORT_LEGAL_DISCLAIMER_HEADING}
      data-legal-paragraph-count={REPORT_LEGAL_DISCLAIMER_PARAGRAPHS.length}
      className="pt-6 space-y-2.5"
      style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}`, ...typographyStyle, ...style }}
    >
      {body}
    </aside>
  );
};
